import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";
import { compressImageIfNeeded } from "@/lib/imageCompress";
import { toast } from "sonner";
import { Upload, Trash2, Star, Loader2, ImagePlus, Play, Home, Square, RectangleHorizontal, Check, X } from "lucide-react";
import { SERVICES } from "@/data/services";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Display "Compressing..." in the upload button when the user has selected
// an image > this size and we're going to recompress it client-side.
const COMPRESS_THRESHOLD_BYTES = 3 * 1024 * 1024;
// How many uploads run in parallel. 2 gives roughly a 2× wall-clock speed-up
// without saturating the ingress (which used to surface as "Network Error").
const UPLOAD_CONCURRENCY = 2;

const PortfolioPanel = () => {
  const { authedAxios, token } = useAuth();
  const { lang, t } = useLanguage();
  const [activeSlug, setActiveSlug] = useState(SERVICES[0].slug);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Per-file upload tasks: { id, name, sizeBytes, status, progress, error? }
  // status ∈ 'pending' | 'compressing' | 'uploading' | 'done' | 'error'
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const fileRef = useRef(null);

  const refreshCounts = async () => {
    const next = {};
    await Promise.all(
      SERVICES.map(async (s) => {
        try {
          const r = await fetch(`${BACKEND}/api/portfolio/${s.slug}`);
          const data = await r.json();
          next[s.slug] = Array.isArray(data) ? data.length : 0;
        } catch {
          next[s.slug] = 0;
        }
      }),
    );
    setCounts(next);
  };

  const loadImages = async (slug) => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/portfolio/${slug}`);
      const data = await r.json();
      setImages(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    queueMicrotask(() => loadImages(activeSlug));
  }, [activeSlug]);

  useEffect(() => {
    queueMicrotask(() => refreshCounts());
  }, []);

  // Global progress — weighted by file size so a 30 MB photo counts more
  // than a 200 KB one. Errored files count as "done" so the overall bar
  // doesn't get stuck if one upload fails mid-batch.
  const globalProgress = useMemo(() => {
    if (tasks.length === 0) return 0;
    const totalBytes = tasks.reduce((s, t) => s + (t.sizeBytes || 1), 0);
    if (totalBytes === 0) return 0;
    const doneBytes = tasks.reduce((s, t) => {
      if (t.status === "done" || t.status === "error") return s + (t.sizeBytes || 1);
      // While uploading, weight by partial progress.
      return s + ((t.sizeBytes || 1) * (t.progress || 0)) / 100;
    }, 0);
    return Math.min(100, Math.round((doneBytes / totalBytes) * 100));
  }, [tasks]);

  const doneCount = useMemo(
    () => tasks.filter((t) => t.status === "done" || t.status === "error").length,
    [tasks],
  );

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Build the per-file task list immediately so the progress UI appears.
    const initialTasks = files.map((f, i) => ({
      id: `${Date.now()}-${i}-${f.name}`,
      name: f.name,
      sizeBytes: f.size,
      status: "pending",
      progress: 0,
      file: f,
    }));
    setTasks(initialTasks);
    setUploading(true);

    const ax = authedAxios();
    let successCount = 0;
    let errorCount = 0;

    const updateTask = (id, patch) =>
      setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));

    const uploadOne = async (task) => {
      try {
        const isImage = task.file.type?.startsWith("image/");
        let payload = task.file;

        // 1. Client-side compression for large images (Nikon Z 15–35 MB JPEGs).
        //    Keeps the ingress happy and slashes upload time.
        if (isImage && task.file.size > COMPRESS_THRESHOLD_BYTES) {
          updateTask(task.id, { status: "compressing", progress: 0 });
          payload = await compressImageIfNeeded(task.file);
        }

        updateTask(task.id, { status: "uploading", progress: 0 });

        const fd = new FormData();
        fd.append("service_slug", activeSlug);
        fd.append("is_cover", "false");
        // FormData boundary is set automatically by axios — DO NOT hardcode
        // Content-Type or the boundary disappears.
        fd.append("file", payload);

        await ax.post("/admin/portfolio/upload", fd, {
          headers: { Authorization: `Bearer ${token}` },
          // Allow up to 5 minutes for slow connections (on-location uploads).
          timeout: 5 * 60 * 1000,
          onUploadProgress: (evt) => {
            if (evt.total) {
              const p = Math.min(100, Math.round((evt.loaded / evt.total) * 100));
              updateTask(task.id, { progress: p });
            }
          },
        });
        updateTask(task.id, { status: "done", progress: 100 });
        successCount += 1;
      } catch (err) {
        const isNetworkError =
          err?.code === "ERR_NETWORK" || (!err?.response && err?.message);
        const detail = isNetworkError
          ? lang === "fr"
            ? "Erreur réseau"
            : "Network error"
          : formatApiErrorDetail(err);
        updateTask(task.id, { status: "error", error: detail });
        toast.error(`${task.name}: ${detail}`);
        errorCount += 1;
      }
    };

    // Worker pool — each worker grabs the next pending task from the shared
    // queue until empty. Gives us a bounded concurrency upload pipeline.
    const queue = [...initialTasks];
    const workers = Array.from(
      { length: Math.min(UPLOAD_CONCURRENCY, queue.length) },
      async () => {
        while (true) {
          const next = queue.shift();
          if (!next) break;
          await uploadOne(next);
        }
      },
    );
    await Promise.all(workers);

    if (successCount > 0) {
      toast.success(
        lang === "fr"
          ? `${successCount} image(s) uploadée(s)`
          : `${successCount} image(s) uploaded`,
      );
    }
    if (fileRef.current) fileRef.current.value = "";
    setUploading(false);
    await loadImages(activeSlug);
    await refreshCounts();
    // Keep the completed task list visible for a couple seconds, then clear
    // it. Errors stay a bit longer so the admin can read them.
    const clearDelay = errorCount > 0 ? 8000 : 2500;
    setTimeout(() => setTasks([]), clearDelay);
  };

  const setCover = async (id) => {
    try {
      const ax = authedAxios();
      await ax.patch(`/admin/portfolio/${id}`, { is_cover: true });
      toast.success(lang === "fr" ? "Définie comme couverture" : "Set as cover");
      await loadImages(activeSlug);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  };

  const toggleHomeFeatured = async (img) => {
    try {
      const ax = authedAxios();
      const next = !img.is_home_featured;
      await ax.patch(`/admin/portfolio/${img.id}`, { is_home_featured: next });
      toast.success(
        lang === "fr"
          ? next ? "Ajoutée à l'accueil" : "Retirée de l'accueil"
          : next ? "Added to home" : "Removed from home",
      );
      await loadImages(activeSlug);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  };

  const toggleOrientation = async (img) => {
    try {
      const ax = authedAxios();
      const next = img.orientation === "vertical" ? "horizontal" : "vertical";
      await ax.patch(`/admin/portfolio/${img.id}`, { orientation: next });
      toast.success(
        lang === "fr"
          ? `Orientation : ${next === "vertical" ? "vertical" : "horizontal"}`
          : `Orientation: ${next}`,
      );
      await loadImages(activeSlug);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  };

  const removeImage = async (id) => {
    if (!window.confirm(lang === "fr" ? "Supprimer cette image ?" : "Delete this image?")) return;
    try {
      const ax = authedAxios();
      await ax.delete(`/admin/portfolio/${id}`);
      toast.success(lang === "fr" ? "Image supprimée" : "Image deleted");
      await loadImages(activeSlug);
      await refreshCounts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  };

  return (
    <section data-testid="admin-portfolio-panel" className="space-y-8">
      {/* Service tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {SERVICES.map((s) => (
          <button
            key={s.slug}
            onClick={() => setActiveSlug(s.slug)}
            data-testid={`admin-portfolio-tab-${s.slug}`}
            className={`text-[10px] uppercase tracking-[0.25em] px-4 py-2.5 border transition-colors ${
              activeSlug === s.slug
                ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5"
                : "border-[#F2EBE5]/15 text-[#A19E9C] hover:text-[#F2EBE5] hover:border-[#F2EBE5]/30"
            }`}
          >
            {t(`services.${s.key}.label`)}{" "}
            <span className="opacity-60">({counts[s.slug] ?? 0})</span>
          </button>
        ))}
      </div>

      {/* Upload */}
      <div className="border-2 border-dashed border-[#F2EBE5]/15 p-10 text-center bg-[#0a0a0a]">
        <ImagePlus className="w-8 h-8 text-[#C5A059] mx-auto mb-4" strokeWidth={1.25} />
        <p className="font-serif-display text-2xl mb-2">
          {lang === "fr"
            ? "Ajouter des images au service"
            : "Add images to service"}{" "}
          <em className="text-[#C5A059]">
            {t(`services.${SERVICES.find((x) => x.slug === activeSlug).key}.label`)}
          </em>
        </p>
        <p className="text-sm text-[#A19E9C] mb-6">
          {lang === "fr"
            ? "Images : JPG, PNG, WEBP (compression auto pour les gros fichiers > 3 Mo). Vidéos : MP4, WEBM, MOV (max 200 Mo). Plusieurs fichiers acceptés."
            : "Images: JPG, PNG, WEBP (auto-compressed when > 3 MB). Videos: MP4, WEBM, MOV (max 200 MB). Multiple files supported."}
        </p>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/webm,video/quicktime,.jpg,.jpeg,.png,.webp,.mp4,.webm,.mov,.m4v"
          onChange={handleUpload}
          disabled={uploading}
          data-testid="admin-portfolio-upload-input"
          className="hidden"
          id="portfolio-upload"
        />
        <label
          htmlFor="portfolio-upload"
          data-testid="admin-portfolio-upload-trigger"
          className="kw-btn-primary cursor-pointer inline-flex disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              {lang === "fr"
                ? `Upload ${doneCount}/${tasks.length} · ${globalProgress}%`
                : `Upload ${doneCount}/${tasks.length} · ${globalProgress}%`}
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" strokeWidth={1.5} />
              {lang === "fr" ? "Sélectionner des images" : "Select images"}
            </>
          )}
        </label>
      </div>

      {/* Progress panel — per-file + global bars while uploads run */}
      {tasks.length > 0 && (
        <div
          data-testid="admin-portfolio-progress"
          className="border border-[#F2EBE5]/15 bg-[#0a0a0a] p-5 space-y-4"
        >
          {/* Global progress */}
          <div>
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] mb-2">
              <span className="text-[#F2EBE5]/80">
                {lang === "fr" ? "Progression globale" : "Overall progress"}
              </span>
              <span
                className="text-[#C5A059] tabular-nums"
                data-testid="admin-portfolio-progress-global"
              >
                {doneCount}/{tasks.length} · {globalProgress}%
              </span>
            </div>
            <div className="h-1.5 bg-[#F2EBE5]/10 overflow-hidden">
              <div
                className="h-full bg-[#C5A059] transition-[width] duration-200 ease-out"
                style={{ width: `${globalProgress}%` }}
              />
            </div>
          </div>

          {/* Per-file rows */}
          <div className="space-y-2.5 max-h-72 overflow-auto pr-1">
            {tasks.map((task) => {
              const isCompressing = task.status === "compressing";
              const isUploading = task.status === "uploading";
              const isDone = task.status === "done";
              const isError = task.status === "error";
              const label = isCompressing
                ? lang === "fr"
                  ? "Compression…"
                  : "Compressing…"
                : isUploading
                ? task.progress >= 100
                  ? lang === "fr"
                    ? "Traitement…"
                    : "Processing…"
                  : `${task.progress}%`
                : isDone
                ? lang === "fr"
                  ? "Terminé"
                  : "Done"
                : isError
                ? lang === "fr"
                  ? "Échec"
                  : "Failed"
                : lang === "fr"
                ? "En attente"
                : "Pending";
              const barWidth = isCompressing
                ? "18%" // indeterminate-ish placeholder during canvas work
                : isDone || isError
                ? "100%"
                : `${task.progress || 0}%`;
              const barColor = isError
                ? "bg-red-500/80"
                : isDone
                ? "bg-emerald-500/80"
                : "bg-[#C5A059]";
              return (
                <div
                  key={task.id}
                  data-testid={`admin-portfolio-progress-row-${task.id}`}
                  className="text-xs"
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="truncate text-[#F2EBE5]/90 flex items-center gap-2 min-w-0">
                      {isDone ? (
                        <Check className="w-3 h-3 text-emerald-400 shrink-0" strokeWidth={2} />
                      ) : isError ? (
                        <X className="w-3 h-3 text-red-400 shrink-0" strokeWidth={2} />
                      ) : isCompressing || isUploading ? (
                        <Loader2 className="w-3 h-3 text-[#C5A059] shrink-0 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <span className="w-3 h-3 shrink-0" />
                      )}
                      <span className="truncate">{task.name}</span>
                    </span>
                    <span
                      className="text-[10px] uppercase tracking-[0.25em] text-[#A19E9C] tabular-nums shrink-0"
                      data-testid={`admin-portfolio-progress-pct-${task.id}`}
                    >
                      {label}
                    </span>
                  </div>
                  <div className="h-1 bg-[#F2EBE5]/8 overflow-hidden">
                    <div
                      className={`h-full transition-[width] duration-200 ease-out ${barColor} ${
                        isCompressing ? "animate-pulse" : ""
                      }`}
                      style={{ width: barWidth }}
                    />
                  </div>
                  {isError && task.error && (
                    <p className="mt-1 text-[10px] text-red-300/80 truncate">{task.error}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Gallery */}
      {loading ? (
        <div className="flex items-center gap-3 text-[#A19E9C] py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-[#C5A059]" strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-[0.3em]">
            {lang === "fr" ? "Chargement…" : "Loading…"}
          </span>
        </div>
      ) : images.length === 0 ? (
        <div className="border border-[#F2EBE5]/10 p-12 text-center">
          <p className="font-serif-display text-2xl text-[#A19E9C]">
            {lang === "fr"
              ? "Aucune image dans cette catégorie."
              : "No images in this category yet."}
          </p>
          <p className="mt-2 text-sm text-[#A19E9C]">
            {lang === "fr"
              ? "Les images de design (Unsplash) restent affichées tant qu'aucune photo n'a été uploadée."
              : "Design images (Unsplash) stay visible until photos are uploaded."}
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
          data-testid="admin-portfolio-grid"
        >
          {images.map((img) => {
            const isVideo = img.media_type === "video";
            const fullUrl = `${BACKEND}${img.url}`;
            const thumbUrl = img.thumb_url ? `${BACKEND}${img.thumb_url}` : fullUrl;
            const aspectCls = img.orientation === "vertical"
              ? "aspect-[4/5]"
              : "aspect-[16/10]";
            return (
              <div
                key={img.id}
                data-testid={`admin-portfolio-img-${img.id}`}
                className={`group relative overflow-hidden bg-[#0a0a0a] border border-[#F2EBE5]/10 ${aspectCls}`}
              >
                {isVideo ? (
                  <>
                    <video
                      src={fullUrl}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                    <div className="absolute top-3 right-3 bg-[#050505]/80 backdrop-blur px-2 py-1 z-10 text-[#C5A059] text-[9px] uppercase tracking-[0.25em] flex items-center gap-1">
                      <Play className="w-3 h-3" strokeWidth={1.5} />
                      Vidéo
                    </div>
                  </>
                ) : (
                  <img
                    src={thumbUrl}
                    alt={img.caption || ""}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                )}
                {img.is_cover && (
                  <div className="absolute top-3 left-3 bg-[#C5A059] text-[#050505] text-[9px] uppercase tracking-[0.25em] px-2 py-1 font-medium z-10">
                    {lang === "fr" ? "Couverture" : "Cover"}
                  </div>
                )}
                {img.is_home_featured && (
                  <div className="absolute top-3 left-3 mt-7 bg-[#F2EBE5] text-[#050505] text-[9px] uppercase tracking-[0.25em] px-2 py-1 font-medium z-10 flex items-center gap-1">
                    <Home className="w-2.5 h-2.5" strokeWidth={2} />
                    {lang === "fr" ? "Accueil" : "Home"}
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-3 z-10">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <button
                      type="button"
                      onClick={() => setCover(img.id)}
                      disabled={img.is_cover}
                      data-testid={`admin-portfolio-cover-${img.id}`}
                      className="text-[10px] uppercase tracking-[0.25em] flex items-center gap-1.5 text-[#F2EBE5] hover:text-[#C5A059] disabled:opacity-40"
                    >
                      <Star className="w-3 h-3" strokeWidth={1.5} />
                      {lang === "fr" ? "Couverture" : "Cover"}
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleHomeFeatured(img)}
                      data-testid={`admin-portfolio-home-${img.id}`}
                      className="text-[10px] uppercase tracking-[0.25em] flex items-center gap-1.5 text-[#F2EBE5] hover:text-[#C5A059]"
                    >
                      <Home className="w-3 h-3" strokeWidth={1.5} />
                      {lang === "fr" ? "Accueil" : "Home"}
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleOrientation(img)}
                      data-testid={`admin-portfolio-orient-${img.id}`}
                      title={lang === "fr" ? "Basculer l'orientation" : "Toggle orientation"}
                      className="text-[10px] uppercase tracking-[0.25em] flex items-center gap-1.5 text-[#F2EBE5] hover:text-[#C5A059]"
                    >
                      {img.orientation === "vertical" ? (
                        <Square className="w-3 h-3" strokeWidth={1.5} />
                      ) : (
                        <RectangleHorizontal className="w-3 h-3" strokeWidth={1.5} />
                      )}
                      {img.orientation === "vertical"
                        ? (lang === "fr" ? "Vertical" : "Vertical")
                        : (lang === "fr" ? "Horizontal" : "Horizontal")}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeImage(img.id)}
                    data-testid={`admin-portfolio-delete-${img.id}`}
                    className="text-[#F2EBE5] hover:text-red-400 self-end"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default PortfolioPanel;
